import { List } from 'immutable';
import { reportError } from 'conversations-error-reporting/error-reporting/reportError';
import { buildAggregateDataReporter } from './buildAggregateDataReporter';
import { withSessionReplay } from 'usage-tracker-session-replay';
export class UsageTrackerError extends Error {}
const logDebug = (...args) => {
  try {
    if (window.localStorage.getItem('TRACKING_DEBUG')) {
      // eslint-disable-next-line no-console
      console.log('TRACKING_DEBUG: ', ...args);
    }
  } catch (error) {
    // do nothing
  }
};
export const createAsyncTracker = (config, loader = () => import( /* webpackChunkName: "async-usage-tracker" */'usage-tracker'), usageTracker) => {
  logDebug('Creating async tracker with config: ', config);
  const asyncTracker = {
    onLoadEventQueue: List(),
    propertiesToSetOnLoad: {},
    tracker: usageTracker,
    loadingPromise: null,
    track(event, properties) {
      if (this.tracker) {
        this.tracker.track(event, properties);
      } else {
        this.onLoadEventQueue = this.onLoadEventQueue.push([event, properties]); // push as a full array
      }
      return this.loadingPromise;
    },
    setProperties(properties) {
      if (this.tracker) {
        this.tracker.setProperties(properties);
      } else {
        this.propertiesToSetOnLoad = Object.assign({}, this.propertiesToSetOnLoad || {}, properties);
      }
      return this.loadingPromise;
    },
    setThreadContext({
      channel
    }) {
      const loadingPromise = this.setProperties({
        channel
      });
      if (loadingPromise) {
        loadingPromise.catch(__error => {});
      }
    },
    clone(overrides = {}) {
      return createAsyncTracker(Object.assign({}, config, overrides), loader, this.tracker);
    },
    initialize() {
      this.loadingPromise = loader();
      return this.loadingPromise.then(({
        createTracker,
        enhanceTrackerWithSessionReplay = withSessionReplay
      }) => {
        this.tracker = enhanceTrackerWithSessionReplay(createTracker(config), {
          // Never show anything a user could possibly enter text into
          maskSelectors: ['input', 'textarea'],
          // Permissively allow text for form/table elements to show
          unmaskSelectors: ['i18n-string', 'label', 'th *'],
          // 1% sample rate for sessions
          sampleRate: 0.01
        });
        this.onLoadEventQueue.forEach(([event, properties]) => this.track(event, properties));
        if (this.propertiesToSetOnLoad) {
          this.tracker.setProperties(this.propertiesToSetOnLoad);
        }
      }).catch(error => {
        reportError({
          error
        });
      });
    }
  };
  asyncTracker.initialize().catch(error => {
    reportError({
      error
    });
  });
  return asyncTracker;
};
export const createStandaloneAsyncTracker = ({
  config,
  loader = () => import( /* webpackChunkName: "async-usage-tracker" */'usage-tracker'),
  namespace,
  standaloneUsageTracker
}) => {
  logDebug('Creating standalone async tracker with config: ', config);
  const asyncTracker = {
    onLoadEventQueue: List(),
    propertiesToSetOnLoad: {},
    tracker: standaloneUsageTracker,
    loadingPromise: null,
    track(event, properties) {
      if (this.tracker) {
        this.tracker.track(namespace, event, properties);
      } else {
        this.onLoadEventQueue = this.onLoadEventQueue.push([event, properties]); // push as a full array
      }
      return this.loadingPromise;
    },
    setProperties(properties) {
      if (this.tracker) {
        this.tracker.setProperties(properties);
      } else {
        this.propertiesToSetOnLoad = Object.assign({}, this.propertiesToSetOnLoad || {}, properties);
      }
      return this.loadingPromise;
    },
    setThreadContext({
      channel
    }) {
      const loadingPromise = this.setProperties({
        channel
      });
      if (loadingPromise) {
        loadingPromise.catch(__error => {});
      }
    },
    clone(overrides = {}) {
      return createStandaloneAsyncTracker({
        config: Object.assign({}, config, overrides),
        loader,
        standaloneUsageTracker: this.tracker,
        namespace
      });
    },
    initialize() {
      this.loadingPromise = loader();
      return this.loadingPromise.then(({
        createTracker,
        enhanceTrackerWithSessionReplay = withSessionReplay
      }) => {
        this.tracker = enhanceTrackerWithSessionReplay(createTracker(config), {
          // Never show anything a user could possibly enter text into
          maskSelectors: ['input', 'textarea'],
          // Permissively allow text for form/table elements to show
          unmaskSelectors: ['i18n-string', 'label', 'th *'],
          // 1% sample rate for sessions
          sampleRate: 0.01
        });
        this.onLoadEventQueue.forEach(([event, properties]) => this.track(event, properties));
        if (this.propertiesToSetOnLoad) {
          this.tracker.setProperties(this.propertiesToSetOnLoad);
        }
      }).catch(error => {
        reportError({
          error
        });
      });
    }
  };
  asyncTracker.initialize().catch(error => {
    reportError({
      error
    });
  });
  return asyncTracker;
};
export const createTrackEvent = getTracker => (event, type = 'trackEvent') => {
  let errorMessage = undefined;
  if (typeof event !== 'object') {
    errorMessage = `${type} expects an object, got ${typeof event}`;
  } else if (!Object.prototype.hasOwnProperty.call(event, 'key') || !Object.prototype.hasOwnProperty.call(event, 'properties')) {
    errorMessage = `${type} expects an object with 'key' and 'properties'`;
  }
  if (errorMessage) {
    window.setTimeout(() => {
      throw new UsageTrackerError(errorMessage);
    }, 0);
    return;
  }
  logDebug('tracking event', event.key, event.properties);
  void getTracker().track(event.key, event.properties);
};
export const createStandaloneTrackEvent = getStandaloneTracker => (event, type = 'trackEvent') => {
  let errorMessage = undefined;
  if (typeof event !== 'object') {
    errorMessage = `${type} expects an object, got ${typeof event}`;
  } else if (!Object.prototype.hasOwnProperty.call(event, 'name') || !Object.prototype.hasOwnProperty.call(event, 'properties')) {
    errorMessage = `${type} expects an object with 'name' and 'properties'`;
  }
  if (errorMessage) {
    window.setTimeout(() => {
      throw new UsageTrackerError(errorMessage);
    }, 0);
    return;
  }
  logDebug('tracking standalone event', event.name, event.properties);
  // namespace does not have to be passed in again because it was instantiated in getStandaloneTracker
  void getStandaloneTracker().track(event.name, event.properties);
};
export const buildTrackerCallbacks = () => {
  const reporter = buildAggregateDataReporter();
  reporter.start();
  return {
    onError(error) {
      reporter.captureEvent(`[error] ${error}`);
    }
  };
};